import React from 'react';
import getConfig from 'next/config';
import { GetServerSideProps } from 'next';
import BasicLayout from 'layout/Basic';
import ContestDetails from 'containers/Contest/Details';
import { convertProps } from 'lib/parseAPIResponse';
import { NextSeo } from 'next-seo';
import ForbiddenPage from 'pages/403';

interface Props {
    ads: PageAds;
    error?: {
        code: number;
        message: string;
    };
    contest: POYContest | QuizContest;
    isExpired: boolean;
}

export const ContestDetailsPage = (props: Props): JSX.Element => {
    if (props?.error?.code === 403) {
        return <ForbiddenPage message="Przepraszamy, ale nie masz dostępu do konkursu." />;
    }

    const {
        contest: { seoSlug, seoTitle, seoDescription, teaser, name }
    } = props;

    const description = seoDescription || teaser?.replace(/<[^>]*>?/gm, '');
    const { url } = getConfig().publicRuntimeConfig;

    return (
        <BasicLayout ads={props.ads}>
            <NextSeo
                noindex={false}
                title={seoTitle || name}
                canonical={`${url}/konkursy/${seoSlug}`}
                description={description}
                openGraph={{
                    title: name,
                    description: description,
                    url: `${url}/konkursy/${seoSlug}`
                }}
            />
            <ContestDetails {...props} />
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params, query }) => {
    let slug = params?.slug || '';

    if (Array.isArray(slug)) {
        slug = slug[0];
    }
    if (slug.includes(',')) {
        slug = slug.substring(0, params.slug.indexOf(','));
    }

    const queryParams = query._previewKey ? `?_previewKey=${query._previewKey}` : '';
    const { apiUrl } = getConfig().publicRuntimeConfig;

    return Promise.all([
        fetch(`${apiUrl}/public/api/contests/${slug}${queryParams}`),
        fetch(`${apiUrl}/public/api/naszadsik?path=contests/${slug}`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        if (data.code === 403) {
            return {
                props: {
                    error: { ...convertProps(data) }
                }
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default ContestDetailsPage;
